import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "./services/api";

export default function AdminOrders({ embedded }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const filteredOrders = statusFilter === "all" 
    ? orders 
    : orders.filter(order => order.status === statusFilter);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await api.get("/orders");
      setOrders(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    setStatusUpdateLoading(orderId);
    try {
      await api.put(`/orders/${orderId}/status`, { status: newStatus });
      
      // Update local state
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      ));
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update status");
    } finally {
      setStatusUpdateLoading(null);
    }
  };

  if (loading) return <div className="p-8 text-center">Loading orders...</div>;
  if (error) return <div className="p-8 text-center text-red-600">Error: {error}</div>;

  return (
    <div className={embedded ? "w-full" : "max-w-6xl mx-auto p-6 pt-24"}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-yellow-600 to-yellow-800 bg-clip-text text-transparent">
          All Orders
        </h1>
        <div className="flex gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
              className="px-5 py-3 bg-raya-card 
              border border-raya-border rounded-xl
              text-raya-text-primary
              focus:outline-none focus:ring-2 focus:ring-raya-accent/50 focus:border-raya-accent
              transition-all duration-300 shadow-sm hover:shadow-md
              font-medium cursor-pointer"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
          </select>
          {!embedded && (
            <Link
              to="/admin"
              className="bg-raya-accent hover:bg-raya-accent-hover
                text-black px-6 py-3 rounded-xl font-semibold
                transition-all duration-300 transform hover:scale-105 active:scale-95
                shadow-md hover:shadow-lg
                flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Back to Dashboard</span>
            </Link>
          )}
        </div>
      </div>

      <div className="bg-raya-card rounded-lg shadow-sm border border-raya-border overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-raya-bg-off shadow-sm">
            <tr>
              <th className="p-4 border-b border-raya-border text-raya-text-primary">Order ID</th>
              <th className="p-4 border-b border-raya-border text-raya-text-primary">Date</th>
              <th className="p-4 border-b border-raya-border text-raya-text-primary">Customer</th>
              <th className="p-4 border-b border-raya-border text-raya-text-primary">Type</th>
              <th className="p-4 border-b border-raya-border text-raya-text-primary text-center">Tip</th>
              <th className="p-4 border-b border-raya-border text-raya-text-primary">Total</th>
              <th className="p-4 border-b border-raya-border text-center text-raya-text-primary">Status</th>
              <th className="p-4 border-b border-raya-border text-raya-text-primary">Items</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((order) => (
              <tr key={order.id} className="hover:bg-raya-accent/5 transition">
                <td className="p-4 border-b border-raya-border text-raya-text-tertiary font-mono">#{order.id}</td>
                <td className="p-4 border-b border-raya-border text-raya-text-secondary">
                  {new Date(order.created_at).toLocaleDateString()}
                </td>
                <td className="p-4 border-b border-raya-border">
                  <div className="font-bold text-raya-text-primary">{order.customer_name || 'Customer'}</div>
                  <div className="text-xs text-raya-text-tertiary">{order.user_email}</div>
                </td>
                <td className="p-4 border-b border-raya-border">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-tighter ${
                    order.order_type === 'local' ? 'bg-raya-accent text-black' : 'bg-blue-100/10 text-blue-400 border border-blue-400/20'
                  }`}>
                    {order.order_type || 'online'}
                  </span>
                  <div className="text-[10px] text-raya-text-tertiary mt-1 opacity-60 italic">{order.payment_method}</div>
                </td>
                <td className="p-4 border-b border-raya-border text-center font-bold text-raya-accent">
                   ${Number(order.tip_amount || 0).toFixed(2)}
                </td>
                <td className="p-4 border-b border-raya-border font-bold text-raya-text-primary">${Number(order.total).toFixed(2)}</td>
                <td className="p-4 border-b border-raya-border text-center">
                  <div className="relative inline-block">
                    {statusUpdateLoading === order.id && (
                      <div className="absolute inset-0 flex items-center justify-center bg-raya-card/50 rounded-full z-10">
                        <div className="w-3 h-3 border-2 border-raya-accent border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                    <select
                      value={order.status}
                      disabled={statusUpdateLoading === order.id}
                      onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                      className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors cursor-pointer focus:ring-2 focus:ring-raya-accent/50 
                        ${order.status === 'delivered' ? 'bg-raya-text-primary text-raya-accent border-raya-accent/30' : 
                          order.status === 'shipped' ? 'bg-raya-accent/10 text-raya-accent border-raya-accent/20' : 
                          'bg-raya-bg-off text-raya-text-secondary border-raya-border'}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                    </select>
                  </div>
                </td>
                <td className="p-4 border-b border-raya-border text-sm text-raya-text-secondary">
                  {order.items && order.items.length > 0 ? (
                    <ul className="list-disc list-inside">
                      {order.items.map((item, idx) => (
                        <li key={idx}>
                          {item.quantity}x {item.name}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-gray-500">No items</span>
                  )}
                </td>
              </tr>
            ))}
            {filteredOrders.length === 0 && (
              <tr>
                <td colSpan="6" className="p-8 text-center text-gray-500">
                  No orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
